import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdTune, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { signalsService } from '../../services/signalsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import MetricCard from '../../components/shared/MetricCard';
import PlotlyChart from '../../components/charts/PlotlyChart';
import SignalBadge from './components/SignalBadge';

const INDICATORS = [
  { key: 'rsi', label: 'RSI' },
  { key: 'macd', label: 'MACD' },
  { key: 'bollinger', label: 'Bollinger' },
  { key: 'stochastic', label: 'Stochastic' },
  { key: 'williams_r', label: 'Williams %R' },
  { key: 'adx', label: 'ADX' },
  { key: 'obv', label: 'OBV' },
  { key: 'volume', label: 'Volume' },
  { key: 'sma_cross', label: 'SMA Cross' },
  { key: 'ema_cross', label: 'EMA Cross' },
  { key: 'price_vs_sma200', label: 'Price vs SMA200' },
  { key: 'fibonacci', label: 'Fibonacci' },
  { key: 'pivot', label: 'Pivot' },
  { key: 'sr', label: 'S/R' },
];

const formatNum = (v, dec = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(dec));

const IndicatorAnalyticsPage = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEtf, setIsEtf] = useState('false');   // default to stocks only
  const [indexFilter, setIndexFilter] = useState('');
  const [sector, setSector] = useState('');
  const [meta, setMeta] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 25 };
      if (isEtf) params.is_etf = isEtf;
      if (indexFilter) params.index = indexFilter;
      if (sector) params.sector = sector;
      const res = await signalsService.getIndicatorScan(name, params);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [name, isEtf, indexFilter, sector]);

  useEffect(() => { load(); }, [load]);

  // Fetch meta once for sector list + index counts.
  useEffect(() => {
    signalsService.getMeta().then(res => setMeta(res.data)).catch(() => {});
  }, []);

  const universeLabel = isEtf === 'false' ? 'Stocks' : isEtf === 'true' ? 'ETFs' : 'Stocks + ETFs';
  const indexLabel = indexFilter === 'sp500' ? 'S&P 500'
    : indexFilter === 'nasdaq100' ? 'Nasdaq 100'
    : indexFilter === 'dow30' ? 'Dow 30'
    : '';
  const filterSummary = [universeLabel, indexLabel, sector].filter(Boolean).join(' · ');

  // Histogram of distribution if numeric reading available
  const histTrace = data?.distribution?.length > 0
    ? [{
        type: 'histogram',
        x: data.distribution,
        marker: { color: '#c9a96e', opacity: 0.85 },
        nbinsx: 30,
      }]
    : null;

  return (
    <div>
      {/* Indicator switcher tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
        {INDICATORS.map((i) => {
          const active = i.key === name;
          return (
            <button
              key={i.key}
              onClick={() => navigate(`/signals/indicator/${i.key}`)}
              className={`text-xs px-3 py-2 whitespace-nowrap transition-colors ${
                active
                  ? 'border-b-2 border-[#c9a96e] text-[#c9a96e]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {i.label}
            </button>
          );
        })}
      </div>

      {/* Filter expander */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg mb-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-xs"
        >
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
            <MdTune className="shrink-0" />
            <span className="uppercase tracking-wider text-[10px] shrink-0">Filters</span>
            <span className="text-[10px] text-gray-400 truncate">· {filterSummary}</span>
          </div>
          {filtersOpen ? <MdExpandLess className="text-gray-400 shrink-0" /> : <MdExpandMore className="text-gray-400 shrink-0" />}
        </button>
        {filtersOpen && (
          <div className="px-3 pb-3 grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 md:items-center text-xs border-t border-gray-100 dark:border-gray-800 pt-3">
            <select
              value={isEtf}
              onChange={(e) => setIsEtf(e.target.value)}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
            >
              <option value="false">Stocks only</option>
              <option value="true">ETFs only</option>
              <option value="">Stocks + ETFs</option>
            </select>
            <select
              value={indexFilter}
              onChange={(e) => setIndexFilter(e.target.value)}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100"
              title="Index membership"
            >
              <option value="">All indices</option>
              <option value="sp500">S&P 500{meta?.index_counts?.sp500 ? ` (${meta.index_counts.sp500})` : ''}</option>
              <option value="nasdaq100">Nasdaq 100{meta?.index_counts?.nasdaq100 ? ` (${meta.index_counts.nasdaq100})` : ''}</option>
              <option value="dow30">Dow 30{meta?.index_counts?.dow30 ? ` (${meta.index_counts.dow30})` : ''}</option>
            </select>
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="px-2 py-1 bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 col-span-2 md:col-span-1"
            >
              <option value="">All sectors</option>
              {(meta?.sectors || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading && <LoadingSpinner message="Computing indicator scan…" />}
      {error && <div className="text-sm text-red-500 mb-4">{error}</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
            <MetricCard label="Universe" value={data.total_symbols || 0} subtitle="scored" />
            <MetricCard label="Bullish" value={data.bullish_count || 0} valueColor="text-green-600 dark:text-green-400" />
            <MetricCard label="Bearish" value={data.bearish_count || 0} valueColor="text-red-500 dark:text-red-400" />
            <MetricCard label="Neutral" value={data.neutral_count || 0} />
          </div>

          {histTrace && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4 mb-4">
              <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-3">
                Universe Distribution — {data.reading_key}
              </div>
              <PlotlyChart
                data={histTrace}
                layout={{
                  height: 240,
                  margin: { l: 50, r: 20, t: 10, b: 30 },
                  yaxis: { tickformat: '.0f' },
                }}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <RankList title="Top Bullish" rows={data.top_bullish || []} accent="green" navigate={navigate} />
            <RankList title="Top Bearish" rows={data.top_bearish || []} accent="red" navigate={navigate} />
          </div>

          {data.ranked?.length > 0 && (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-3">
                Full Ranking by {data.reading_key || 'composite score'}
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-gray-400 tracking-wider">
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="py-2 text-left">Symbol</th>
                      <th className="py-2 text-left">Name</th>
                      <th className="py-2 text-left">Sector</th>
                      <th className="py-2 text-right">Reading</th>
                      <th className="py-2 text-center">Vote</th>
                      <th className="py-2 text-center">Signal</th>
                      <th className="py-2 text-right">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ranked.slice(0, 200).map((r) => (
                      <tr
                        key={r.symbol}
                        onClick={() => navigate(`/signals/symbol/${r.symbol}`)}
                        className="border-b border-gray-100 dark:border-gray-900 hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer"
                      >
                        <td className="py-1.5 font-semibold text-[#c9a96e]">{r.symbol}</td>
                        <td className="py-1.5 text-gray-700 dark:text-gray-300 truncate max-w-[14rem]">{r.name}</td>
                        <td className="py-1.5 text-gray-500 dark:text-gray-400 truncate max-w-[10rem]">{r.sector || '—'}</td>
                        <td className="py-1.5 text-right text-gray-900 dark:text-gray-100">{formatNum(r.reading, 3)}</td>
                        <td className={`py-1.5 text-center ${r.vote > 0 ? 'text-green-600 dark:text-green-400' : r.vote < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-500'}`}>
                          {r.vote > 0 ? '+1' : r.vote < 0 ? '−1' : '0'}
                        </td>
                        <td className="py-1.5 text-center"><SignalBadge signal={r.signal} /></td>
                        <td className="py-1.5 text-right">{formatNum(r.score, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RankList = ({ title, rows, accent, navigate }) => {
  const headerColor = accent === 'green'
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-500 dark:text-red-400';
  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
      <div className={`text-[10px] uppercase tracking-[2px] mb-3 ${headerColor}`}>{title}</div>
      <div className="space-y-1">
        {rows.length === 0 && <div className="text-xs text-gray-400">—</div>}
        {rows.map((r) => (
          <div
            key={r.symbol}
            onClick={() => navigate(`/signals/symbol/${r.symbol}`)}
            className="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-900 cursor-pointer hover:text-[#c9a96e]"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="font-semibold text-[#c9a96e] w-12 shrink-0">{r.symbol}</span>
              <span className="text-gray-500 dark:text-gray-400 truncate">{r.name}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-gray-700 dark:text-gray-300">
                {r.reading == null ? '' : Number(r.reading).toFixed(2)}
              </span>
              <SignalBadge signal={r.signal} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndicatorAnalyticsPage;
